import React, { memo, useEffect, useState, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { createStructuredSelector } from 'reselect'
import { Button } from 'reactstrap'
import moment from 'moment'

import { openError } from 'containers/App/reducers'
import Header from '../components/Header'
import RowButton from '../components/RowButton'
import { SpRowButton } from '../components/SpRowButton'
import PrevStartButton from '../components/PrevStartButton'
import PageNumber from '../components/PageNumber'
import { formatBookingNo } from 'utils/formatters'
import * as S from '../style'
import { getListBookingNotCheckin } from '../api'
import { useSelector, useDispatch } from 'reduxStoreHelper'
import useIsSp from 'hooks/useIsSp'
import {
  phoneNumberEnteredSelectors,
  isCreatedBookingDataSelectors,
  driverConfirmationsSelectors,
} from '../selectors'
import type { GeneralBookingType } from '../type'
import {
  clearBooking,
  startLoading,
  endLoading,
  setIsFromBookingNo,
  resetCommonData,
  setSelectedBooking,
  resetSelectedBooking,
} from '../reducer'
import { INTEGRATED_RECEPTION_URL_BASE } from '../constants'
import type { ConfirmationType } from 'containers/IntegratedReceptionCustom/type'
import { getStatusCodesAndErrorCode } from 'utils/commonFunctions'

type Props = {
  generalReceptionId?: string
  driverConfirmations?: ConfirmationType[]
  canGeneralBooking?: boolean
}

const EntryBookingsPage: React.FC<Props> = ({
  generalReceptionId,
  driverConfirmations,
  canGeneralBooking,
}) => {
  const [bookings, setBookings] = useState<GeneralBookingType[]>([])
  const { generalReceptionNumber }: { generalReceptionNumber: string } = useParams()

  const history = useHistory()
  const dispatch = useDispatch()
  const isSp = useIsSp()
  const phoneNumberEntered = useSelector(phoneNumberEnteredSelectors())
  const isCreatedBookingData = useSelector(isCreatedBookingDataSelectors())

  const filteredConfirmations = driverConfirmations?.filter(
    (confirmation: ConfirmationType) => confirmation?.displayFlag
  )

  useEffect(() => {
    ;(async () => {
      dispatch(startLoading())
      try {
        const res = await getListBookingNotCheckin(generalReceptionId, phoneNumberEntered)
        setBookings(res?.data)
      } catch (error) {
        const { errorCodes, status } = getStatusCodesAndErrorCode(error)
        //@ts-ignore
        dispatch(openError(error.message, true, errorCodes, status))
      } finally {
        dispatch(endLoading())
      }
    })()
  }, [dispatch, generalReceptionId, phoneNumberEntered])

  const handleClickBookingNo = useCallback(
    async (booking: GeneralBookingType) => {
      dispatch(setSelectedBooking({ ...booking }))
      dispatch(setIsFromBookingNo(true))
      history.push(`${INTEGRATED_RECEPTION_URL_BASE}/entry-driver-info/${generalReceptionNumber}`)
    },
    [dispatch, generalReceptionNumber, history]
  )

  useEffect(() => {
    if (isCreatedBookingData) {
      dispatch(resetCommonData())
      dispatch(resetSelectedBooking())
      dispatch(clearBooking())
    }
  }, [dispatch, isCreatedBookingData])

  const handleClickCheckinNew = () => {
    dispatch(clearBooking())
    dispatch(setIsFromBookingNo(false))
    history.push(`${INTEGRATED_RECEPTION_URL_BASE}/entry-driver-info/${generalReceptionNumber}`)
  }

  // NOTE: PC の予約行。RowButton は総合受付側の既存部品（generalBookingNo を描画）。SP は下の spBookingRows を使う
  const bookingRows = bookings.map((booking) => (
    <div key={booking.bookingNo} className="mb-3">
      {/* @ts-ignore */}
      <RowButton
        onClick={(bookingDetail) => {
          handleClickBookingNo(bookingDetail)
        }}
        booking={booking}
      >
        <>
          <span>{moment(booking.planStartDate).format('M')}</span>
          <span style={{ fontSize: 18 }}>月</span>
          <span>{moment(booking.planStartDate).format('D')}</span>
          <span className="mr-2" style={{ fontSize: 18 }}>
            日
          </span>
          <span style={{ fontSize: 18 }}>
            {booking.planStartTime
              ? moment(
                  `${booking.planStartDate} ${booking.planStartTime}`,
                  'YYYYMMDD HH:mm'
                ).format('HH:mm')
              : '--:--'}
            <span className="px-1">〜</span>
            {booking.planEndTime
              ? moment(`${booking.planEndDate} ${booking.planEndTime}`, 'YYYYMMDD HH:mm').format(
                  'HH:mm'
                )
              : '--:--'}
          </span>
        </>
      </RowButton>
    </div>
  ))

  const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土']
  // SP: 通常予約 SpRowButton に合わせ、日付＋曜日＋時間帯を1行目、予約番号を下段に置く（右は矢印のみ）
  const spBookingRows = bookings.map((booking) => {
    const start = booking.planStartTime
      ? moment(`${booking.planStartDate} ${booking.planStartTime}`, 'YYYYMMDD HH:mm').format(
          'HH:mm'
        )
      : '--:--'
    const end = booking.planEndTime
      ? moment(`${booking.planEndDate} ${booking.planEndTime}`, 'YYYYMMDD HH:mm').format('HH:mm')
      : '--:--'
    return (
      <div key={booking.bookingNo} className="mb-3">
        <SpRowButton onClick={() => handleClickBookingNo(booking)}>
          <div style={{ marginTop: -8 }}>
            <div>
              <span style={{ fontSize: 16 }}>{moment(booking.planStartDate).format('M')}</span>
              <span style={{ fontSize: 16 }}>月</span>
              <span style={{ fontSize: 16 }}>{moment(booking.planStartDate).format('D')}</span>
              <span className="mr-2" style={{ fontSize: 16 }}>
                日（{WEEKDAYS[moment(booking.planStartDate).day()]}）
              </span>
              <span style={{ fontSize: 16 }}>
                {start}
                <span className="px-1">〜</span>
                {end}
              </span>
            </div>
            <div style={{ fontSize: 16, marginTop: -4, textAlign: 'center' }}>
              {formatBookingNo(booking.generalBookingNo)}
            </div>
          </div>
        </SpRowButton>
      </div>
    )
  })

  // SP（スマホ縦長）: 通常受付 SP と同思想で全体を縦積みに。戻るを上部、フッターボタンはフルワイド縦積み。
  if (isSp) {
    return (
      <S.Wrapper>
        <Header />
        <div className="p-3">
          <div className="mb-3">
            <PrevStartButton
              onClick={() =>
                history.push(`${INTEGRATED_RECEPTION_URL_BASE}/${generalReceptionNumber}`)
              }
              data-dg-id="sp-entry-bookings-page-prev-start-button"
            />
          </div>

          <div className="bg-white p-3" style={{ borderRadius: 12 }}>
            <div
              className="mb-3 text-center font-weight-bold"
              style={{ fontSize: 20 }}
              data-dg-id="sp-entry-bookings-page-title"
            >
              予約の確認
            </div>

            {bookings?.length ? (
              <div data-dg-id="sp-entry-bookings-page-bookings-list">{spBookingRows}</div>
            ) : (
              <div className="text-center" data-dg-id="sp-entry-bookings-page-no-bookings">
                あなたの電話番号で予約はありません
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="mb-3">
              <Button
                data-dg-id="sp-entry-bookings-page-button-checkin-by-booking-number"
                color="primary"
                outline
                className="px-4 rounded-pill bg-white text-primary w-100"
                style={{ height: 56, fontSize: 16 }}
                onClick={() =>
                  history.push(
                    `${INTEGRATED_RECEPTION_URL_BASE}/entry-booking-number/${generalReceptionNumber}`
                  )
                }
              >
                リストにない予約番号で受付
              </Button>
            </div>
            <Button
              data-dg-id="sp-entry-bookings-page-button-checkin-no-booking"
              color="primary"
              className="px-4 rounded-pill w-100"
              style={{ height: 56, fontSize: 16 }}
              onClick={handleClickCheckinNew}
            >
              予約なし受付
            </Button>

            <div className="text-center mt-4">
              <PageNumber
                page="entry-bookings"
                existsDriverConfirmations={Boolean(filteredConfirmations?.length)}
                isFreePlan={!canGeneralBooking}
              />
            </div>
          </div>
        </div>
      </S.Wrapper>
    )
  }

  return (
    <S.Wrapper>
      <Header />
      <S.EntryBookingPage className="pt-3 px-3">
        <div className="bg-white p-3" style={{ borderRadius: 12 }}>
          <div className="text-center mx-auto" style={{ width: 504 }}>
            <div
              className="mb-3 text-size32 font-weight-bold"
              data-dg-id="entry-bookings-page-title"
            >
              予約の確認
            </div>

            {bookings?.length ? (
              <>
                <div className="mb-3" data-dg-id="entry-bookings-page-bookings-exist">
                  あなたの電話番号で以下の予約があります
                </div>
                <div className="d-flex mb-3 border-bottom">
                  <div style={{ marginLeft: 54 }}>予約日時</div>
                  <div style={{ marginLeft: 162 }}>予約番号</div>
                </div>
              </>
            ) : (
              <div className="mb-3" data-dg-id="entry-bookings-page-no-bookings">
                あなたの電話番号で予約はありません
              </div>
            )}
          </div>

          {Boolean(bookings?.length) && (
            <div
              className="mx-auto"
              style={{ width: 504 }}
              data-dg-id="entry-bookings-page-bookings-list"
            >
              {bookingRows}
            </div>
          )}

          <div
            className="p-3"
            style={{
              position: 'fixed',
              backgroundColor: '#ededed',
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <div className="d-flex justify-content-center pb-3">
              <Button
                data-dg-id="entry-bookings-page-button-checkin-by-booking-number"
                color="primary"
                outline
                className="px-4 mr-3 text-size24 rounded-pill bg-white text-primary"
                style={{ height: 52 }}
                onClick={() =>
                  history.push(
                    `${INTEGRATED_RECEPTION_URL_BASE}/entry-booking-number/${generalReceptionNumber}`
                  )
                }
              >
                リストにない予約番号で受付
              </Button>
              <Button
                data-dg-id="entry-bookings-page-button-checkin-no-booking"
                color="primary"
                outline
                className="px-4 text-size24 rounded-pill bg-white text-primary"
                style={{ height: 52 }}
                onClick={handleClickCheckinNew}
              >
                予約なし受付
              </Button>
            </div>
            <div className="d-flex justify-content-between">
              <div style={{ width: '33%' }}>
                <PrevStartButton
                  onClick={() =>
                    history.push(`${INTEGRATED_RECEPTION_URL_BASE}/${generalReceptionNumber}`)
                  }
                  data-dg-id="entry-bookings-page-prev-start-button"
                />
              </div>
              <div className="text-center" style={{ width: '34%' }}>
                <PageNumber
                  page="entry-bookings"
                  existsDriverConfirmations={Boolean(filteredConfirmations?.length)}
                  isFreePlan={!canGeneralBooking}
                />
              </div>
              <div className="text-right" style={{ width: '33%' }} />
            </div>
          </div>
        </div>
      </S.EntryBookingPage>
    </S.Wrapper>
  )
}

const mapStateToProps = createStructuredSelector({
  driverConfirmations: driverConfirmationsSelectors(),
})

const withConnect = connect(mapStateToProps, null)

export default compose(withConnect)(memo(EntryBookingsPage))
